import { 
  collection, 
  doc, 
  getDoc, 
  getDocs, 
  addDoc, 
  setDoc,
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  orderBy, 
  limit, 
  onSnapshot,
  serverTimestamp,
  arrayUnion,
  arrayRemove,
  increment
} from 'firebase/firestore';
import { 
  ref, 
  uploadBytes, 
  getDownloadURL, 
  deleteObject 
} from 'firebase/storage';
import { db, storage } from '../config/firebase';
import { authService } from './authService';

// Helper function to upload image to Firebase Storage
async function uploadImageToStorage(imageBase64, path, fileName) {
  try {
    if (!imageBase64) return null;
    
    // Convert base64 to blob
    const response = await fetch(imageBase64);
    const blob = await response.blob();
    
    // Get the original file extension from the blob type
    let fileExtension = 'jpg'; // default fallback
    if (blob.type) {
      const mimeType = blob.type.toLowerCase();
      if (mimeType.includes('jpeg') || mimeType.includes('jpg')) {
        fileExtension = 'jpg';
      } else if (mimeType.includes('png')) {
        fileExtension = 'png';
      } else if (mimeType.includes('gif')) {
        fileExtension = 'gif';
      } else if (mimeType.includes('webp')) {
        fileExtension = 'webp';
      } else if (mimeType.includes('bmp')) {
        fileExtension = 'bmp';
      } else if (mimeType.includes('svg')) {
        fileExtension = 'svg';
      } else if (mimeType.includes('pdf')) {
        fileExtension = 'pdf';
      }
    }
    
    // Create storage reference with proper file extension
    const storageRef = ref(storage, `${path}/${fileName}.${fileExtension}`);
    
    // Upload to Firebase Storage
    const snapshot = await uploadBytes(storageRef, blob);
    
    // Get download URL
    const downloadURL = await getDownloadURL(snapshot.ref);
    
    return downloadURL;
  } catch (error) {
    console.error(`Error uploading image ${fileName}:`, error);
    
    // If it's a permission error, log it but don't fail the entire process
    if (error.code === 'storage/unauthorized') {
      console.warn(`Storage permission denied for ${fileName}. This is expected if Firebase Storage rules are not configured.`);
      console.warn('Images will be stored as base64 in Firestore (this may increase document size).');
      console.warn('To fix this, configure Firebase Storage rules to allow authenticated users to upload files.');
      return null; // Return null to indicate upload failed, will fall back to base64
    }
    
    return null;
  }
}

// Helper function to get user-specific booking keys
const getUserBookingKeys = () => {
  const currentUser = JSON.parse(localStorage.getItem('currentUser') || '{}');
  const userId = currentUser.uid || currentUser.id || 'anonymous';
  return {
    userId,
    userBookingsKey: `userBookings_${userId}`,
    generalBookingsKey: `bookings_${userId}`
  };
};

// User Service
export const userService = {
  // Create user profile
  async createUser(userData) {
    try {
      const userRef = doc(db, 'users', userData.uid);
      await setDoc(userRef, {
        ...userData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      return { success: true, data: userData };
    } catch (error) {
      console.error('Create user error:', error);
      return { success: false, error: error.message };
    }
  },

  // Get user by ID
  async getUserById(uid) {
    try {
      const userDoc = await getDoc(doc(db, 'users', uid));
      if (userDoc.exists()) {
        return { success: true, data: { id: userDoc.id, ...userDoc.data() } };
      }
      return { success: false, error: 'User not found' };
    } catch (error) {
      console.error('Get user error:', error);
      return { success: false, error: error.message };
    }
  },

  // Update user
  async updateUser(uid, updates) {
    try {
      const userRef = doc(db, 'users', uid);
      await updateDoc(userRef, {
        ...updates,
        updatedAt: serverTimestamp()
      });
      return { success: true };
    } catch (error) {
      console.error('Update user error:', error);
      return { success: false, error: error.message };
    }
  },

  // Delete user
  async deleteUser(uid) {
    try {
      await deleteDoc(doc(db, 'users', uid));
      return { success: true };
    } catch (error) {
      console.error('Delete user error:', error);
      return { success: false, error: error.message };
    }
  },

  // Get user profile (alias for getUserById)
  async getUserProfile(uid) {
    return this.getUserById(uid);
  },

  // Upsert user profile (create or update)
  async upsertUserProfile(uid, userData) {
    try {
      console.log('🔄 Starting profile upsert for user:', uid);
      console.log('📋 Profile data:', userData);
      
      // Handle profile picture upload if it's a base64 string
      let profilePictureUrl = userData.profile_picture || userData.profile_pic; // Check both fields
      if (profilePictureUrl && profilePictureUrl.startsWith('data:')) {
        console.log('📸 Uploading profile picture to storage...');
        profilePictureUrl = await uploadImageToStorage(
          profilePictureUrl,
          'user-profiles',
          `${uid}/profile-picture`
        );
        
        if (profilePictureUrl) {
          console.log('✅ Profile picture uploaded successfully:', profilePictureUrl);
        } else {
          console.log('⚠️ Profile picture upload failed, keeping as base64');
          profilePictureUrl = userData.profile_picture || userData.profile_pic; // Keep original base64
        }
      }
      
      // Prepare the data to save, filtering out undefined values
      const profileData = {
        updatedAt: serverTimestamp()
      };
      
      // Copy userData fields, excluding undefined values
      Object.keys(userData).forEach(key => {
        if (userData[key] !== undefined) {
          profileData[key] = userData[key];
        }
      });
      
      // Only add profile picture fields if they have values
      if (profilePictureUrl !== undefined) {
        profileData.profile_picture = profilePictureUrl;
        profileData.profile_pic = profilePictureUrl; // Also save to profile_pic field for compatibility
      }
      
      console.log('💾 Saving profile data to Firestore...');
      console.log('📋 Final profile data to save:', profileData);
      const userRef = doc(db, 'users', uid);
      await setDoc(userRef, profileData, { merge: true }); // merge: true will update existing or create new
      
      console.log('✅ Profile saved successfully');
      return { success: true, data: profileData };
    } catch (error) {
      console.error('❌ Upsert user profile error:', error);
      return { success: false, error: error.message };
    }
  }
};

// Lawyer Service
export const lawyerService = {
  // Create lawyer profile with optimized image handling
  async createLawyer(lawyerData) {
    try {
      // Check if a lawyer profile already exists with this email
      if (lawyerData.email) {
        console.log('🔍 Checking for existing lawyer profile with email:', lawyerData.email);
        
        const existingLawyerQuery = query(
          collection(db, 'lawyers'),
          where('email', '==', lawyerData.email)
        );
        
        const existingLawyerSnapshot = await getDocs(existingLawyerQuery);
        
        if (!existingLawyerSnapshot.empty) {
          console.log('❌ Lawyer profile already exists with this email');
          return { 
            success: false, 
            error: 'A lawyer profile already exists with this email address. Only one profile per email is allowed.' 
          };
        }
        
        console.log('✅ No existing lawyer profile found with this email');
      }

      // Extract image data that needs to be uploaded to Storage
      const imagesToUpload = {
        profilePic: lawyerData.profile_pic,
        idProof: lawyerData.id_proof,
        barCouncilCert: lawyerData.bar_council_certificate,
        selfieWithId: lawyerData.selfie_with_id,
        faceVerification: lawyerData.face_verification_image,
        governmentId: lawyerData.government_id
      };

      // Upload images to Firebase Storage and get URLs
      // Ensure we have a valid UID - this should be the Firebase Auth UID
      const actualUid = lawyerData.uid;
      if (!actualUid) {
        throw new Error('No UID provided for lawyer profile creation');
      }
      const imageUrls = {};
      
      if (imagesToUpload.profilePic) {
        imageUrls.profilePicUrl = await uploadImageToStorage(
          imagesToUpload.profilePic, 
          'lawyer-profiles', 
          `${actualUid}/profile-pic`
        );
      }
      
      if (imagesToUpload.idProof) {
        imageUrls.idProofUrl = await uploadImageToStorage(
          imagesToUpload.idProof, 
          'lawyer-documents', 
          `${actualUid}/id-proof`
        );
      }
      
      if (imagesToUpload.barCouncilCert) {
        imageUrls.barCouncilCertUrl = await uploadImageToStorage(
          imagesToUpload.barCouncilCert, 
          'lawyer-documents', 
          `${actualUid}/bar-council-cert`
        );
      }
      
      if (imagesToUpload.selfieWithId) {
        imageUrls.selfieWithIdUrl = await uploadImageToStorage(
          imagesToUpload.selfieWithId, 
          'lawyer-documents', 
          `${actualUid}/selfie-with-id`
        );
      }
      
      if (imagesToUpload.faceVerification) {
        imageUrls.faceVerificationUrl = await uploadImageToStorage(
          imagesToUpload.faceVerification, 
          'lawyer-verification', 
          `${actualUid}/face-verification`
        );
      }
      
      if (imagesToUpload.governmentId) {
        imageUrls.governmentIdUrl = await uploadImageToStorage(
          imagesToUpload.governmentId, 
          'lawyer-documents', 
          `${actualUid}/government-id`
        );
      }

      // Create optimized lawyer data with fallback to base64 if uploads failed
      const optimizedLawyerData = {
        ...lawyerData,
        // Replace base64 images with URLs, fall back to base64 if upload failed
        profile_pic: imageUrls.profilePicUrl || lawyerData.profile_pic || '',
        id_proof: imageUrls.idProofUrl || lawyerData.id_proof || '',
        bar_council_certificate: imageUrls.barCouncilCertUrl || lawyerData.bar_council_certificate || '',
        selfie_with_id: imageUrls.selfieWithIdUrl || lawyerData.selfie_with_id || '',
        face_verification_image: imageUrls.faceVerificationUrl || lawyerData.face_verification_image || '',
        government_id: imageUrls.governmentIdUrl || lawyerData.government_id || '',
        // Add image URLs for easy access
        imageUrls: imageUrls
      };

      // Remove any remaining base64 data to ensure document size is under limit
      const cleanData = JSON.parse(JSON.stringify(optimizedLawyerData));
      
      const lawyerRef = doc(db, 'lawyers', actualUid);
      await setDoc(lawyerRef, {
        ...cleanData,
        uid: actualUid, // Ensure UID is set in the document
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        isVerified: false,
        rating: 0,
        totalReviews: 0
      });
      
      return { success: true, data: optimizedLawyerData };
    } catch (error) {
      console.error('Create lawyer error:', error);
      return { success: false, error: error.message };
    }
  },

  // Get lawyer by ID
  async getLawyerById(uid) {
    try {
      const lawyerDoc = await getDoc(doc(db, 'lawyers', uid));
      if (lawyerDoc.exists()) {
        return { success: true, data: { id: lawyerDoc.id, ...lawyerDoc.data() } };
      }
      return { success: false, error: 'Lawyer not found' };
    } catch (error) {
      console.error('Get lawyer error:', error);
      return { success: false, error: error.message };
    }
  },

  // Get lawyer by user ID
  async getLawyerByUserId(userId) {
    try {
      const lawyersQuery = query(collection(db, 'lawyers'), where('uid', '==', userId));
      const querySnapshot = await getDocs(lawyersQuery);
      
      if (!querySnapshot.empty) {
        const lawyerDoc = querySnapshot.docs[0];
        return { success: true, data: { id: lawyerDoc.id, ...lawyerDoc.data() } };
      }
      return { success: false, error: 'Lawyer not found' };
    } catch (error) {
      console.error('Get lawyer by user ID error:', error);
      return { success: false, error: error.message };
    }
  },

  // Get all lawyers
  async getAllLawyers() {
    try {
      const lawyersQuery = query(collection(db, 'lawyers'), where('verificationStatus', '==', 'approved'));
      const querySnapshot = await getDocs(lawyersQuery);
      const lawyers = [];
      querySnapshot.forEach((doc) => {
        lawyers.push({ id: doc.id, ...doc.data() });
      });
      return { success: true, data: lawyers };
    } catch (error) {
      console.error('Get all lawyers error:', error);
      return { success: false, error: error.message };
    }
  },

  // Search lawyers
  async searchLawyers(filters = {}) {
    try {
      console.log('🔍 Searching lawyers with filters:', filters);
      
      let lawyersQuery = query(collection(db, 'lawyers'), where('isVerified', '==', true));
      
      if (filters.specialization) {
        lawyersQuery = query(lawyersQuery, where('specialization', 'array-contains', filters.specialization));
      }
      
      if (filters.city) {
        lawyersQuery = query(lawyersQuery, where('bar_council_state', '==', filters.city));
      }
      
      if (filters.experience) {
        const minExperience = parseInt(filters.experience);
        lawyersQuery = query(lawyersQuery, where('experience_years', '>=', minExperience));
      }

      const querySnapshot = await getDocs(lawyersQuery);
      const lawyers = [];
      querySnapshot.forEach((doc) => {
        const lawyerData = { id: doc.id, ...doc.data() };
        console.log('📋 Found lawyer:', lawyerData.full_name || lawyerData.fullName, 'Verified:', lawyerData.isVerified);
        lawyers.push(lawyerData);
      });
      
      console.log(`✅ Found ${lawyers.length} verified lawyers`);
      return { success: true, data: lawyers };
    } catch (error) {
      console.error('Search lawyers error:', error);
      return { success: false, error: error.message };
    }
  },

  // Update lawyer
  async updateLawyer(uid, updates) {
    try {
      const lawyerRef = doc(db, 'lawyers', uid);
      await updateDoc(lawyerRef, {
        ...updates,
        updatedAt: serverTimestamp()
      });
      return { success: true };
    } catch (error) {
      console.error('Update lawyer error:', error);
      return { success: false, error: error.message };
    }
  },

  // Get all lawyers for admin verification
  async getAllLawyersForVerification() {
    try {
      const lawyersRef = collection(db, 'lawyers');
      const q = query(lawyersRef, orderBy('createdAt', 'desc'));
      const querySnapshot = await getDocs(q);
      
      const lawyers = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      return { success: true, data: lawyers };
    } catch (error) {
      console.error('Get all lawyers error:', error);
      return { success: false, error: error.message };
    }
  },

  // Update lawyer verification status
  async updateLawyerVerification(lawyerId, verificationData) {
    try {
      const lawyerRef = doc(db, 'lawyers', lawyerId);
      const updateData = {
        verificationStatus: verificationData.status,
        verificationDate: new Date().toISOString(),
        verificationNotes: verificationData.notes || '',
        verifiedBy: verificationData.adminId,
        updated_at: new Date().toISOString()
      };
      
      await updateDoc(lawyerRef, updateData);
      
      return { success: true, data: { id: lawyerId, ...updateData } };
    } catch (error) {
      console.error('Update lawyer verification error:', error);
      return { success: false, error: error.message };
    }
  }
};

// Appointment/Booking Service
export const appointmentService = {
  // Create appointment
  async createAppointment(appointmentData) {
    try {
      const appointmentRef = await addDoc(collection(db, 'appointments'), {
        ...appointmentData,
        status: 'pending',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      
      // Create chat room for this appointment
      try {
        const chatData = {
          appointmentId: appointmentRef.id,
          lawyerId: appointmentData.lawyer_id,
          clientId: appointmentData.client_id,
          participants: [appointmentData.lawyer_id, appointmentData.client_id],
          type: 'consultation',
          status: 'active'
        };
        
        const chatRef = await addDoc(collection(db, 'chats'), {
          ...chatData,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
        
        // Send initial message from system
        await addDoc(collection(db, 'chats', chatRef.id, 'messages'), {
          text: `Booking created for consultation on ${appointmentData.appointment_date} at ${appointmentData.start_time}. Waiting for lawyer confirmation.`,
          senderId: 'system',
          type: 'system',
          createdAt: serverTimestamp()
        });
        
        console.log('✅ Chat room created for appointment:', chatRef.id);
      } catch (chatError) {
        console.error('❌ Error creating chat room:', chatError);
      }
      
      return { success: true, data: { id: appointmentRef.id, ...appointmentData } };
    } catch (error) {
      console.error('Create appointment error:', error);
      return { success: false, error: error.message };
    }
  },

  // Create appointment with payment confirmation
  async createAppointmentWithPayment(appointmentData, paymentData) {
    try {
      // First create the payment record
      const paymentRef = await addDoc(collection(db, 'payments'), {
        ...paymentData,
        status: 'completed',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });

      // Then create the appointment with confirmed status
      const appointmentRef = await addDoc(collection(db, 'appointments'), {
        ...appointmentData,
        status: 'confirmed',
        paymentId: paymentRef.id,
        paymentStatus: 'completed',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });

      return { 
        success: true, 
        data: { 
          appointmentId: appointmentRef.id, 
          paymentId: paymentRef.id,
          ...appointmentData 
        } 
      };
    } catch (error) {
      console.error('Create appointment with payment error:', error);
      return { success: false, error: error.message };
    }
  },

  // Get appointment by ID
  async getAppointmentById(appointmentId) {
    try {
      const appointmentDoc = await getDoc(doc(db, 'appointments', appointmentId));
      if (appointmentDoc.exists()) {
        return { success: true, data: { id: appointmentDoc.id, ...appointmentDoc.data() } };
      }
      return { success: false, error: 'Appointment not found' };
    } catch (error) {
      console.error('Get appointment error:', error);
      return { success: false, error: error.message };
    }
  },

  // Get user appointments
  async getUserAppointments(userId, role = 'client') {
    try {
      const field = role === 'lawyer' ? 'lawyer_id' : 'client_id';
      const appointmentsQuery = query(
        collection(db, 'appointments'),
        where(field, '==', userId)
      );
      const querySnapshot = await getDocs(appointmentsQuery);
      const appointments = [];
      querySnapshot.forEach((doc) => {
        appointments.push({ id: doc.id, ...doc.data() });
      });
      
      // Sort client-side to avoid index requirement
      appointments.sort((a, b) => {
        const dateA = a.createdAt?.toDate?.() || new Date(a.createdAt) || new Date(0);
        const dateB = b.createdAt?.toDate?.() || new Date(b.createdAt) || new Date(0);
        return dateB - dateA;
      });
      
      return { success: true, data: appointments };
    } catch (error) {
      console.error('Get user appointments error:', error);
      return { success: false, error: error.message };
    }
  },

  // Update appointment
  async updateAppointment(appointmentId, updates) {
    try {
      const appointmentRef = doc(db, 'appointments', appointmentId);
      await updateDoc(appointmentRef, {
        ...updates,
        updatedAt: serverTimestamp()
      });
      return { success: true };
    } catch (error) {
      console.error('Update appointment error:', error);
      return { success: false, error: error.message };
    }
  },

  // Cancel appointment
  async cancelAppointment(appointmentId) {
    try {
      const appointmentRef = doc(db, 'appointments', appointmentId);
      await updateDoc(appointmentRef, {
        status: 'cancelled',
        updatedAt: serverTimestamp()
      });
      return { success: true };
    } catch (error) {
      console.error('Cancel appointment error:', error);
      return { success: false, error: error.message };
    }
  },

  // Accept appointment (for lawyers)
  async acceptAppointment(appointmentId, lawyerId) {
    try {
      const appointmentRef = doc(db, 'appointments', appointmentId);
      await updateDoc(appointmentRef, {
        status: 'confirmed',
        acceptedAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });

      // Send confirmation message in chat
      try {
        const chatsQuery = query(
          collection(db, 'chats'),
          where('appointmentId', '==', appointmentId)
        );
        const chatSnapshot = await getDocs(chatsQuery);
        
        if (!chatSnapshot.empty) {
          const chatDoc = chatSnapshot.docs[0];
          await addDoc(collection(db, 'chats', chatDoc.id, 'messages'), {
            text: '✅ Your consultation has been confirmed by the lawyer! You can now proceed with your consultation.',
            senderId: 'system',
            type: 'system',
            createdAt: serverTimestamp()
          });
        }
      } catch (chatError) {
        console.error('❌ Error sending confirmation message:', chatError);
      }

      return { success: true };
    } catch (error) {
      console.error('Accept appointment error:', error);
      return { success: false, error: error.message };
    }
  },

  // Reject appointment (for lawyers)
  async rejectAppointment(appointmentId, lawyerId, reason = '') {
    try {
      const appointmentRef = doc(db, 'appointments', appointmentId);
      await updateDoc(appointmentRef, {
        status: 'rejected',
        rejectedAt: serverTimestamp(),
        rejectionReason: reason,
        updatedAt: serverTimestamp()
      });

      // Send rejection message in chat
      try {
        const chatsQuery = query(
          collection(db, 'chats'),
          where('appointmentId', '==', appointmentId)
        );
        const chatSnapshot = await getDocs(chatsQuery);
        
        if (!chatSnapshot.empty) {
          const chatDoc = chatSnapshot.docs[0];
          await addDoc(collection(db, 'chats', chatDoc.id, 'messages'), {
            text: `❌ Your consultation request has been rejected.${reason ? ` Reason: ${reason}` : ''}`,
            senderId: 'system',
            type: 'system',
            createdAt: serverTimestamp()
          });
        }
      } catch (chatError) {
        console.error('❌ Error sending rejection message:', chatError);
      }

      return { success: true };
    } catch (error) {
      console.error('Reject appointment error:', error);
      return { success: false, error: error.message };
    }
  },

  // Get pending appointments for lawyer
  async getPendingAppointments(lawyerId) {
    try {
      const appointmentsQuery = query(
        collection(db, 'appointments'),
        where('lawyer_id', '==', lawyerId),
        where('status', '==', 'pending'),
        orderBy('createdAt', 'desc')
      );
      const querySnapshot = await getDocs(appointmentsQuery);
      const appointments = [];
      querySnapshot.forEach((doc) => {
        appointments.push({ id: doc.id, ...doc.data() });
      });
      return { success: true, data: appointments };
    } catch (error) {
      console.error('Get pending appointments error:', error);
      return { success: false, error: error.message };
    }
  },

  // Get lawyer appointments
  async getLawyerAppointments(lawyerId) {
    try {
      const appointmentsQuery = query(
        collection(db, 'appointments'),
        where('lawyer_id', '==', lawyerId),
        orderBy('createdAt', 'desc')
      );
      const querySnapshot = await getDocs(appointmentsQuery);
      const appointments = [];
      querySnapshot.forEach((doc) => {
        appointments.push({ id: doc.id, ...doc.data() });
      });
      return { success: true, data: appointments };
    } catch (error) {
      console.error('Get lawyer appointments error:', error);
      return { success: false, error: error.message };
    }
  }
};

// Chat Service
export const chatService = {
  // Create chat room with authentication - with duplicate prevention
  async createChatRoom(chatData) {
    try {
      // Ensure user is authenticated
      const user = await authService.getCurrentUser();
      if (!user) {
        throw new Error('User must be registered and signed in to create chats');
      }

      // First, check if a chat already exists for this client-lawyer combination
      if (chatData.lawyerId) {
        console.log('🔍 Checking for existing chat between client:', user.uid, 'and lawyer:', chatData.lawyerId);
        
        const existingChatsQuery = query(
          collection(db, 'chats'),
          where("clientId", "==", user.uid),
          where("lawyerId", "==", chatData.lawyerId),
          where("status", "in", ["active", "pending"])
        );
        
        const existingChatsSnapshot = await getDocs(existingChatsQuery);
        
        if (!existingChatsSnapshot.empty) {
          // Chat already exists, return the existing chat ID
          const existingChat = existingChatsSnapshot.docs[0];
          console.log('🔄 Chat already exists, returning existing chat ID:', existingChat.id);
          return { 
            success: true, 
            isExisting: true,
            data: { id: existingChat.id, ...existingChat.data() }
          };
        }
      }
      
      // No existing chat found, create a new one
      const chatRef = await addDoc(collection(db, 'chats'), {
        ...chatData,
        clientId: user.uid,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      return { success: true, isExisting: false, data: { id: chatRef.id, ...chatData } };
    } catch (error) {
      console.error('Create chat error:', error);
      return { success: false, error: error.message };
    }
  },

  // Clean up duplicate chats automatically
  async cleanupDuplicateChats() {
    try {
      console.log('🧹 Starting automatic duplicate chat cleanup...');
      
      const user = await authService.getCurrentUser();
      if (!user) {
        console.log('⚠️ No user authenticated, skipping cleanup');
        return { success: false, error: 'User not authenticated' };
      }
      
      // Get all chats for the current user
      const chatsRef = collection(db, 'chats');
      const userChatsQuery = query(
        chatsRef,
        where("clientId", "==", user.uid)
      );
      
      const userChatsSnapshot = await getDocs(userChatsQuery);
      
      if (userChatsSnapshot.empty) {
        console.log('📭 No chats found for user');
        return { success: true, message: 'No chats to clean up' };
      }
      
      console.log(`📊 Found ${userChatsSnapshot.size} chats for user`);
      
      // Group chats by lawyer
      const lawyerGroups = new Map();
      
      userChatsSnapshot.forEach((chatDoc) => {
        const chatData = chatDoc.data();
        const lawyerId = chatData.lawyerId;
        
        if (!lawyerGroups.has(lawyerId)) {
          lawyerGroups.set(lawyerId, []);
        }
        lawyerGroups.get(lawyerId).push({
          id: chatDoc.id,
          ...chatData
        });
      });
      
      let totalDuplicates = 0;
      let totalMerged = 0;
      
      // Process each lawyer group
      for (const [lawyerId, chats] of lawyerGroups) {
        if (chats.length > 1) {
          console.log(`🔄 Found ${chats.length} chats for lawyer ${lawyerId}`);
          
          // Sort by creation date (oldest first)
          chats.sort((a, b) => {
            const dateA = a.createdAt?.toDate?.() || new Date(a.createdAt || 0);
            const dateB = b.createdAt?.toDate?.() || new Date(b.createdAt || 0);
            return dateA - dateB;
          });
          
          // Keep the oldest chat, delete others
          const primaryChat = chats[0];
          const duplicateChats = chats.slice(1);
          
          console.log(`   Keeping primary chat: ${primaryChat.id}`);
          console.log(`   Deleting ${duplicateChats.length} duplicate chats`);
          
          // Delete duplicate chats
          for (const duplicateChat of duplicateChats) {
            try {
              await deleteDoc(doc(db, 'chats', duplicateChat.id));
              console.log(`     🗑️ Deleted duplicate chat: ${duplicateChat.id}`);
              totalMerged++;
            } catch (deleteError) {
              console.error(`     ❌ Error deleting duplicate chat ${duplicateChat.id}:`, deleteError);
            }
          }
          
          totalDuplicates += duplicateChats.length;
        }
      }
      
      console.log(`🎉 Cleanup completed: ${totalDuplicates} duplicates found, ${totalMerged} deleted`);
      return { 
        success: true, 
        duplicatesFound: totalDuplicates, 
        duplicatesDeleted: totalMerged 
      };
      
    } catch (error) {
      console.error('❌ Error during duplicate chat cleanup:', error);
      return { success: false, error: error.message };
    }
  },

  // Get chat by ID
  async getChatById(chatId) {
    try {
      const chatDoc = await getDoc(doc(db, 'chats', chatId));
      if (chatDoc.exists()) {
        return { success: true, data: { id: chatDoc.id, ...chatDoc.data() } };
      }
      return { success: false, error: 'Chat not found' };
    } catch (error) {
      console.error('Get chat error:', error);
      return { success: false, error: error.message };
    }
  },

  // Get user chats with authentication
  async getUserChats(userId = null) {
    try {
      // Get current user if not provided
      if (!userId) {
        const user = await authService.getCurrentUser();
        if (!user) {
          throw new Error('User must be registered and signed in to view chats');
        }
        userId = user.uid;
      }

      const chatsQuery = query(
        collection(db, 'chats'),
        where('participants', 'array-contains', userId),
        orderBy('updatedAt', 'desc')
      );
      const querySnapshot = await getDocs(chatsQuery);
      const chats = [];
      querySnapshot.forEach((doc) => {
        chats.push({ id: doc.id, ...doc.data() });
      });
      return { success: true, data: chats };
    } catch (error) {
      console.error('Get user chats error:', error);
      return { success: false, error: error.message };
    }
  },

  // Send message with authentication
  async sendMessage(chatId, messageData) {
    try {
      // Ensure user is authenticated
      const user = await authService.getCurrentUser();
      if (!user) {
        throw new Error('User must be registered and signed in to send messages');
      }

      const messageRef = await addDoc(collection(db, 'chats', chatId, 'messages'), {
        ...messageData,
        senderId: user.uid,
        createdAt: serverTimestamp()
      });
      
      // Update chat's last message
      const chatRef = doc(db, 'chats', chatId);
      await updateDoc(chatRef, {
        lastMessage: messageData.text,
        lastMessageTime: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      
      return { success: true, data: { id: messageRef.id, ...messageData } };
    } catch (error) {
      console.error('Send message error:', error);
      return { success: false, error: error.message };
    }
  },

  // Get chat messages
  async getChatMessages(chatId) {
    try {
      const messagesQuery = query(
        collection(db, 'chats', chatId, 'messages'),
        orderBy('createdAt', 'asc')
      );
      const querySnapshot = await getDocs(messagesQuery);
      const messages = [];
      querySnapshot.forEach((doc) => {
        messages.push({ id: doc.id, ...doc.data() });
      });
      return { success: true, data: messages };
    } catch (error) {
      console.error('Get chat messages error:', error);
      return { success: false, error: error.message };
    }
  },

  // Send automated message
  async sendAutomatedMessage(chatId, messageType, bookingData = {}) {
    try {
      // Ensure user is authenticated
      const user = await authService.getCurrentUser();
      if (!user) {
        throw new Error('User must be registered and signed in to send automated messages');
      }

      let messageText = '';
      let messageData = {
        type: 'automated',
        messageType: messageType,
        createdAt: serverTimestamp()
      };

      switch (messageType) {
        case 'booking_confirmed':
          messageText = `🎉 **Booking Confirmed Successfully!**

📋 **Booking ID:** ${bookingData.bookingId || 'N/A'}
👨‍💼 **Lawyer:** ${bookingData.lawyerName || 'Unknown Lawyer'}
📅 **Date:** ${bookingData.date || 'Not specified'}
⏰ **Time:** ${bookingData.time || 'Not specified'}
⏱️ **Duration:** ${bookingData.duration || 60} minutes
💼 **Services:** ${Array.isArray(bookingData.consultationTypes) ? bookingData.consultationTypes.join(', ') : (bookingData.consultationTypes || 'General Consultation')}

💰 **Payment Details:**
• Consultation Fee: ₹${bookingData.consultationFees || 0}
• Service Charge: ₹${bookingData.serviceCharge || 200}
• **Total Amount:** ₹${bookingData.totalAmount || 0}

✅ Your booking is now pending lawyer approval. You'll be notified once the lawyer accepts your consultation request.

💬 You can start chatting with the lawyer to discuss your case details.`;
          messageData.content = messageText;
          break;
        case 'booking_reminder':
          messageText = `⏰ **Appointment Reminder**

Your consultation is scheduled for tomorrow at ${bookingData.time || 'the specified time'}. Please ensure you have all necessary documents ready.

📋 **Details:**
• Lawyer: ${bookingData.lawyerName || 'Your lawyer'}
• Date: ${bookingData.date || 'Tomorrow'}
• Time: ${bookingData.time || 'As scheduled'}

Please arrive 10 minutes early for your consultation.`;
          messageData.content = messageText;
          break;
        case 'booking_cancelled':
          messageText = `❌ **Booking Cancelled**

Your consultation scheduled for ${bookingData.date || 'the specified date'} has been cancelled.

📋 **Cancellation Details:**
• Lawyer: ${bookingData.lawyerName || 'Your lawyer'}
• Date: ${bookingData.date || 'As scheduled'}
• Time: ${bookingData.time || 'As scheduled'}

Please contact support if you have any questions about the cancellation.`;
          messageData.content = messageText;
          break;
        case 'chat_started':
          messageText = `👋 **Welcome to Your Legal Consultation Chat!**

Your chat session has started. How can we help you with your legal matter today?

💡 **Tips for a productive consultation:**
• Be clear about your legal issue
• Have relevant documents ready
• Ask specific questions
• Take notes during the conversation`;
          messageData.content = messageText;
          break;
        default:
          messageText = `📝 **Automated Message**

Message type: ${messageType}

If you have any questions, please contact support.`;
          messageData.content = messageText;
      }

      const messageRef = await addDoc(collection(db, 'chats', chatId, 'messages'), {
        ...messageData,
        senderId: 'system',
        createdAt: serverTimestamp()
      });
      
      // Update chat's last message
      const chatRef = doc(db, 'chats', chatId);
      await updateDoc(chatRef, {
        lastMessage: messageText,
        lastMessageTime: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      
      return { success: true, data: { id: messageRef.id, ...messageData } };
    } catch (error) {
      console.error('Send automated message error:', error);
      return { success: false, error: error.message };
    }
  },

  // Mark messages as read
  async markMessagesAsRead(chatId, userId) {
    try {
      // Ensure user is authenticated
      const user = await authService.getCurrentUser();
      if (!user) {
        throw new Error('User must be registered and signed in to mark messages as read');
      }

      // Update chat document to mark messages as read
      const chatRef = doc(db, 'chats', chatId);
      await updateDoc(chatRef, {
        unreadCount: 0,
        lastReadBy: userId,
        lastReadAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      
      return { success: true };
    } catch (error) {
      console.error('Mark messages as read error:', error);
      return { success: false, error: error.message };
    }
  },

  // Update chat status
  async updateChatStatus(chatId, status) {
    try {
      const chatRef = doc(db, 'chats', chatId);
      await updateDoc(chatRef, {
        status: status,
        updatedAt: serverTimestamp()
      });
      
      return { success: true };
    } catch (error) {
      console.error('Update chat status error:', error);
      return { success: false, error: error.message };
    }
  }
};

// Enhanced Booking Service
export const bookingService = {
  // Create booking with user authentication
  async createBooking(bookingData) {
    try {
      // Ensure user is authenticated
      const user = await authService.getCurrentUser();
      if (!user) {
        throw new Error('User must be registered and signed in to create bookings');
      }

      // Get user profile
      const profileResult = await authService.getUserProfile(user.uid);
      if (!profileResult.success) {
        throw new Error('User profile not found');
      }

      const booking = {
        ...bookingData,
        clientId: user.uid,
        status: 'pending',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      const bookingRef = await addDoc(collection(db, 'bookings'), booking);
      console.log('✅ Booking created successfully:', bookingRef.id);

      // Store in localStorage for offline access
      const { userId, userBookingsKey } = getUserBookingKeys();
      const existingBookings = JSON.parse(localStorage.getItem(userBookingsKey) || '[]');
      existingBookings.push({ id: bookingRef.id, ...booking });
      localStorage.setItem(userBookingsKey, JSON.stringify(existingBookings));

      return { success: true, data: { id: bookingRef.id, ...booking } };
    } catch (error) {
      console.error('❌ Error creating booking:', error);
      
      // No fallback for unauthenticated users
      return { success: false, error: 'User must be registered and signed in to create bookings' };
    }
  },

  // Get user bookings with fallback to localStorage
  async getUserBookings(userId = null) {
    try {
      // Get current user if not provided
      if (!userId) {
        const user = await authService.getCurrentUser();
        userId = user?.uid;
      }

      if (!userId) {
        // No fallback for unauthenticated users
        return { success: false, error: 'User must be registered and signed in to view bookings' };
      }

      // Try to get from Firebase
      const { userId: currentUserId, generalBookingsKey } = getUserBookingKeys();
      const bookingsQuery = query(
        collection(db, 'bookings'),
        where('clientId', '==', userId),
        orderBy('createdAt', 'desc')
      );

      const querySnapshot = await getDocs(bookingsQuery);
      const bookings = [];
      
      querySnapshot.forEach((doc) => {
        bookings.push({ id: doc.id, ...doc.data() });
      });

      // Also get from localStorage and merge
      const localBookings = JSON.parse(localStorage.getItem(generalBookingsKey) || '[]');
      const allBookings = [...bookings, ...localBookings];
      
      // Remove duplicates based on ID
      const uniqueBookings = allBookings.filter((booking, index, self) => 
        index === self.findIndex(b => b.id === booking.id)
      );

      return { success: true, data: uniqueBookings };
    } catch (error) {
      console.error('❌ Error getting user bookings:', error);
      
      // No fallback for unauthenticated users
      return { success: false, error: 'User must be registered and signed in to view bookings' };
    }
  },

  // Update booking status
  async updateBookingStatus(bookingId, status) {
    try {
      const user = await authService.getCurrentUser();
      if (!user) {
        throw new Error('User authentication failed');
      }

      const { userId, userBookingsKey } = getUserBookingKeys();
      const bookingRef = doc(db, 'bookings', bookingId);
      await updateDoc(bookingRef, {
        status,
        updatedAt: serverTimestamp()
      });

      // Update localStorage
      const existingBookings = JSON.parse(localStorage.getItem(userBookingsKey) || '[]');
      const updatedBookings = existingBookings.map(booking => 
        booking.id === bookingId ? { ...booking, status, updatedAt: new Date().toISOString() } : booking
      );
      localStorage.setItem(userBookingsKey, JSON.stringify(updatedBookings));

      return { success: true };
    } catch (error) {
      console.error('❌ Error updating booking status:', error);
      
      // Fallback: update localStorage only
      try {
        const { userId, userBookingsKey } = getUserBookingKeys();
        const existingBookings = JSON.parse(localStorage.getItem(userBookingsKey) || '[]');
        const updatedBookings = existingBookings.map(booking => 
          booking.id === bookingId ? { ...booking, status, updatedAt: new Date().toISOString() } : booking
        );
        localStorage.setItem(userBookingsKey, JSON.stringify(updatedBookings));
        return { success: true, fallback: true };
      } catch (fallbackError) {
        return { success: false, error: error.message };
      }
    }
  }
};

// Real-time Chat Service
export const realtimeChatService = {
  // Subscribe to chat messages
  subscribeToChatMessages(chatId, callback) {
    try {
      console.log('🔍 Setting up onSnapshot for chat messages, chatId:', chatId);
      
      // Subscribe to messages in the correct subcollection
      const messagesQuery = query(
        collection(db, 'chats', chatId, 'messages'),
        orderBy('createdAt', 'asc')
      );
      
      return onSnapshot(messagesQuery, (snapshot) => {
        console.log('🔍 Chat messages snapshot received:', snapshot.docs.length, 'messages');
        console.log('🔍 Snapshot docs:', snapshot.docs);
        
        const messages = [];
        snapshot.forEach((doc) => {
          messages.push({ id: doc.id, ...doc.data() });
        });
        
        console.log('🔍 Processed messages:', messages);
        // Messages are already sorted by createdAt in the query
        callback(messages);
      }, (error) => {
        console.error('❌ Error in onSnapshot for chat messages:', error);
        console.error('❌ Error details:', {
          code: error.code,
          message: error.message,
          stack: error.stack
        });
        // Fallback: return empty array
        callback([]);
      });
    } catch (error) {
      console.error('❌ Error setting up chat messages subscription:', error);
      // Fallback: return empty array
      callback([]);
    }
  },

  // Subscribe to user chats
  subscribeToUserChats(userId, callback) {
    const chatsQuery = query(
      collection(db, 'chats'),
      where('participants', 'array-contains', userId)
    );
    
    return onSnapshot(chatsQuery, (snapshot) => {
      const chats = [];
      snapshot.forEach((doc) => {
        chats.push({ id: doc.id, ...doc.data() });
      });
      callback(chats);
    });
  },

  // Subscribe to all lawyer availability updates
  subscribeToAllLawyerAvailability(callback) {
    const lawyersQuery = query(
      collection(db, 'lawyers'),
      where('isVerified', '==', true)
    );
    
    return onSnapshot(lawyersQuery, (snapshot) => {
      snapshot.docChanges().forEach((change) => {
        if (change.type === 'modified') {
          const lawyerData = { id: change.doc.id, ...change.doc.data() };
          callback(lawyerData);
        }
      });
    });
  },

  // Unsubscribe from real-time subscriptions
  unsubscribe(subscription) {
    if (subscription && typeof subscription === 'function') {
      subscription();
    }
  },

  // Subscribe to lawyer availability updates
  subscribeToLawyerAvailability(lawyerId, callback) {
    try {
      const lawyerRef = doc(db, 'lawyers', lawyerId);
      return onSnapshot(lawyerRef, (doc) => {
        if (doc.exists()) {
          const lawyerData = { id: doc.id, ...doc.data() };
          callback(lawyerData);
        }
      });
    } catch (error) {
      console.error('Error subscribing to lawyer availability:', error);
      return null;
    }
  },

  // Subscribe to dashboard metrics updates
  subscribeToDashboardMetrics(lawyerId, callback) {
    try {
      // Subscribe to appointments collection for this lawyer
      const appointmentsQuery = query(
        collection(db, 'appointments'),
        where('lawyerId', '==', lawyerId)
      );
      
      return onSnapshot(appointmentsQuery, (snapshot) => {
        const changes = snapshot.docChanges();
        if (changes.length > 0) {
          callback({ eventType: 'UPDATE', changes });
        }
      });
    } catch (error) {
      console.error('Error subscribing to dashboard metrics:', error);
      return null;
    }
  },

  // Subscribe to appointment status updates
  subscribeToAppointmentStatus(lawyerId, callback) {
    try {
      const appointmentsQuery = query(
        collection(db, 'appointments'),
        where('lawyerId', '==', lawyerId)
      );
      
      return onSnapshot(appointmentsQuery, (snapshot) => {
        snapshot.docChanges().forEach((change) => {
          if (change.type === 'added') {
            callback({ eventType: 'INSERT', new: { id: change.doc.id, ...change.doc.data() } });
          } else if (change.type === 'modified') {
            const oldData = change.doc.metadata?.hasPendingWrites ? {} : change.doc.data();
            callback({ 
              eventType: 'UPDATE', 
              new: { id: change.doc.id, ...change.doc.data() },
              old: oldData
            });
          }
        });
      });
    } catch (error) {
      console.error('Error subscribing to appointment status:', error);
      return null;
    }
  },

  // Subscribe to case status updates
  subscribeToCaseStatusUpdates(lawyerId, callback) {
    try {
      const casesQuery = query(
        collection(db, 'appointments'),
        where('lawyerId', '==', lawyerId)
      );
      
      return onSnapshot(casesQuery, (snapshot) => {
        snapshot.docChanges().forEach((change) => {
          if (change.type === 'modified') {
            const oldData = change.doc.metadata?.hasPendingWrites ? {} : change.doc.data();
            callback({ 
              new: { id: change.doc.id, ...change.doc.data() },
              old: oldData
            });
          }
        });
      });
    } catch (error) {
      console.error('Error subscribing to case status updates:', error);
      return null;
    }
  },

  // Subscribe to case activity
  subscribeToCaseActivity(lawyerId, callback) {
    try {
      const casesQuery = query(
        collection(db, 'appointments'),
        where('lawyerId', '==', lawyerId)
      );
      
      return onSnapshot(casesQuery, (snapshot) => {
        if (snapshot.docChanges().length > 0) {
          callback({ eventType: 'ACTIVITY', timestamp: new Date() });
        }
      });
    } catch (error) {
      console.error('Error subscribing to case activity:', error);
      return null;
    }
  },

  // NEW: Subscribe to earnings updates
  subscribeToEarningsUpdates(lawyerId, callback) {
    try {
      const appointmentsQuery = query(
        collection(db, 'appointments'),
        where('lawyerId', '==', lawyerId),
        where('status', '==', 'completed')
      );
      
      return onSnapshot(appointmentsQuery, (snapshot) => {
        if (snapshot.docChanges().length > 0) {
          callback({ eventType: 'EARNINGS_UPDATE', timestamp: new Date() });
        }
      });
    } catch (error) {
      console.error('Error subscribing to earnings updates:', error);
      return null;
    }
  },

  // NEW: Subscribe to client updates
  subscribeToClientUpdates(lawyerId, callback) {
    try {
      const appointmentsQuery = query(
        collection(db, 'appointments'),
        where('lawyerId', '==', lawyerId)
      );
      
      return onSnapshot(appointmentsQuery, (snapshot) => {
        if (snapshot.docChanges().length > 0) {
          callback({ eventType: 'CLIENT_UPDATE', timestamp: new Date() });
        }
      });
    } catch (error) {
      console.error('Error subscribing to client updates:', error);
      return null;
    }
  },

  // NEW: Subscribe to pending case updates
  subscribeToPendingCaseUpdates(lawyerId, callback) {
    try {
      const pendingQuery = query(
        collection(db, 'appointments'),
        where('lawyerId', '==', lawyerId),
        where('status', '==', 'pending')
      );
      
      return onSnapshot(pendingQuery, (snapshot) => {
        if (snapshot.docChanges().length > 0) {
          callback({ eventType: 'PENDING_CASE_UPDATE', timestamp: new Date() });
        }
      });
    } catch (error) {
      console.error('Error subscribing to pending case updates:', error);
      return null;
    }
  },

  // NEW: Subscribe to message updates
  subscribeToMessageUpdates(lawyerId, callback) {
    try {
      const chatsQuery = query(
        collection(db, 'chats'),
        where('lawyerId', '==', lawyerId)
      );
      
      return onSnapshot(chatsQuery, (snapshot) => {
        if (snapshot.docChanges().length > 0) {
          callback({ eventType: 'MESSAGE_UPDATE', timestamp: new Date() });
        }
      });
    } catch (error) {
      console.error('Error subscribing to message updates:', error);
      return null;
    }
  },

  // NEW: Subscribe to consultation type updates
  subscribeToConsultationTypeUpdates(lawyerId, callback) {
    try {
      const appointmentsQuery = query(
        collection(db, 'appointments'),
        where('lawyerId', '==', lawyerId)
      );
      
      return onSnapshot(appointmentsQuery, (snapshot) => {
        if (snapshot.docChanges().length > 0) {
          callback({ eventType: 'CONSULTATION_TYPE_UPDATE', timestamp: new Date() });
        }
      });
    } catch (error) {
      console.error('Error subscribing to consultation type updates:', error);
      return null;
    }
  },

  // NEW: Subscribe to appointment updates
  subscribeToAppointmentUpdates(lawyerId, callback) {
    try {
      const appointmentsQuery = query(
        collection(db, 'appointments'),
        where('lawyerId', '==', lawyerId)
      );
      
      return onSnapshot(appointmentsQuery, (snapshot) => {
        if (snapshot.docChanges().length > 0) {
          callback({ eventType: 'APPOINTMENT_UPDATE', timestamp: new Date() });
        }
      });
    } catch (error) {
      console.error('Error subscribing to appointment updates:', error);
      return null;
    }
  },

  // NEW: Subscribe to performance updates
  subscribeToPerformanceUpdates(lawyerId, callback) {
    try {
      const appointmentsQuery = query(
        collection(db, 'appointments'),
        where('lawyerId', '==', lawyerId)
      );
      
      return onSnapshot(appointmentsQuery, (snapshot) => {
        if (snapshot.docChanges().length > 0) {
          callback({ eventType: 'PERFORMANCE_UPDATE', timestamp: new Date() });
        }
      });
    } catch (error) {
      console.error('Error subscribing to performance updates:', error);
      return null;
    }
  },

  // NEW: Subscribe to recent case updates
  subscribeToRecentCaseUpdates(lawyerId, callback) {
    try {
      const appointmentsQuery = query(
        collection(db, 'appointments'),
        where('lawyerId', '==', lawyerId)
      );
      
      return onSnapshot(appointmentsQuery, (snapshot) => {
        if (snapshot.docChanges().length > 0) {
          callback({ eventType: 'RECENT_CASE_UPDATE', timestamp: new Date() });
        }
      });
    } catch (error) {
      console.error('Error subscribing to recent case updates:', error);
      return null;
    }
  },

  // NEW: Subscribe to wallet updates
  subscribeToWalletUpdates(lawyerId, callback) {
    try {
      const walletQuery = query(
        collection(db, 'lawyer_wallets'),
        where('lawyerId', '==', lawyerId)
      );
      
      return onSnapshot(walletQuery, (snapshot) => {
        if (snapshot.docChanges().length > 0) {
          callback({ eventType: 'WALLET_UPDATE', timestamp: new Date() });
        }
      });
    } catch (error) {
      console.error('Error subscribing to wallet updates:', error);
      return null;
    }
  }
};

// Lawyer Search Service
export const lawyerSearchService = {
  // Search lawyers with filters
  async searchLawyers(filters = {}) {
    try {
      console.log('🔍 LawyerSearchService: Starting lawyer search...');
      console.log('🔍 Filters:', filters);
      
      // Check if Firebase is properly initialized
      if (!db) {
        console.error('❌ Firebase database not initialized');
        return { success: false, error: 'Firebase database not initialized' };
      }
      
      // First, let's check if there are any lawyers at all in the database
      console.log('🔍 Checking for existing lawyers in database...');
      let allLawyersQuery = query(collection(db, 'lawyers'));
      let allLawyersSnapshot;
      
      try {
        allLawyersSnapshot = await getDocs(allLawyersQuery);
        console.log(`📊 Total lawyers in database: ${allLawyersSnapshot.size}`);
      } catch (error) {
        console.error('❌ Error fetching lawyers from database:', error);
        return { success: false, error: `Database error: ${error.message}` };
      }
      
      // If no lawyers exist, create some sample lawyers
      if (allLawyersSnapshot.size === 0) {
        console.log('🆕 No lawyers found in database, creating sample lawyers...');
        try {
          await this.createSampleLawyers();
          // Fetch again after creating sample data
          allLawyersSnapshot = await getDocs(allLawyersQuery);
          console.log(`📊 Lawyers after creation: ${allLawyersSnapshot.size}`);
        } catch (error) {
          console.error('❌ Error creating sample lawyers:', error);
          return { success: false, error: `Failed to create sample lawyers: ${error.message}` };
        }
      }
      
      // Now build the filtered query
      console.log('🔍 Building filtered query...');
      let lawyersQuery = query(collection(db, 'lawyers'));
      
      // Only add verification filter if we have verified lawyers
      const verifiedLawyers = allLawyersSnapshot.docs.filter(doc => {
        const data = doc.data();
        return data.isVerified === true || data.verificationStatus === 'approved';
      });
      
      console.log(`📊 Verified lawyers found: ${verifiedLawyers.length}`);
      
      if (verifiedLawyers.length > 0) {
        // If we have verified lawyers, filter by verification
        console.log('🔍 Filtering by verified status...');
        lawyersQuery = query(
          collection(db, 'lawyers'), 
          where('isVerified', '==', true)
        );
      } else {
        // If no verified lawyers, show all lawyers
        console.log('⚠️ No verified lawyers found, showing all lawyers');
        lawyersQuery = query(collection(db, 'lawyers'));
      }
      
      if (filters.specialization) {
        console.log('🔍 Adding specialization filter:', filters.specialization);
        lawyersQuery = query(lawyersQuery, where('specialization', 'array-contains', filters.specialization));
      }
      
      if (filters.city) {
        console.log('🔍 Adding city filter:', filters.city);
        lawyersQuery = query(lawyersQuery, where('bar_council_state', '==', filters.city));
      }
      
      if (filters.experience) {
        const minExperience = parseInt(filters.experience);
        console.log('🔍 Adding experience filter:', minExperience);
        lawyersQuery = query(lawyersQuery, where('experience_years', '>=', minExperience));
      }

      if (filters.rating) {
        const minRating = parseFloat(filters.rating);
        console.log('🔍 Adding rating filter:', minRating);
        lawyersQuery = query(lawyersQuery, where('average_rating', '>=', minRating));
      }

      console.log('🔍 Executing final query...');
      const querySnapshot = await getDocs(lawyersQuery);
      const lawyers = [];
      
      console.log(`📊 Query returned ${querySnapshot.size} documents`);
      
      querySnapshot.forEach((doc) => {
        const lawyerData = { id: doc.id, ...doc.data() };
        console.log('📋 LawyerSearchService: Found lawyer:', {
          id: doc.id,
          name: lawyerData.full_name || lawyerData.fullName,
          verified: lawyerData.isVerified,
          verificationStatus: lawyerData.verificationStatus,
          specialization: lawyerData.specialization,
          location: lawyerData.bar_council_state,
          bar_council_number: lawyerData.bar_council_number,
          chamber_address: lawyerData.chamber_address
        });
        lawyers.push(lawyerData);
      });
      
      console.log(`✅ LawyerSearchService: Successfully found ${lawyers.length} lawyers`);
      return { success: true, data: lawyers };
    } catch (error) {
      console.error('❌ LawyerSearchService: Search lawyers error:', error);
      console.error('❌ Error details:', {
        message: error.message,
        code: error.code,
        stack: error.stack
      });
      return { success: false, error: error.message };
    }
  },

  // Create sample lawyers if none exist
  async createSampleLawyers() {
    try {
      console.log('🆕 Creating sample lawyers...');
      
      const sampleLawyers = [
        {
          full_name: 'Adv. Priya Sharma',
          fullName: 'Adv. Priya Sharma',
          email: 'priya.sharma@example.com',
          phone: '+91 98765 43210',
          specialization: ['Criminal Law', 'Family Law'],
          bar_council_state: 'Mumbai, Maharashtra',
          location: 'Mumbai, Maharashtra',
          experience_years: 12,
          average_rating: 4.8,
          consultation_fee: 2500,
          fees: {
            text: 1500,
            call: 2000,
            video: 2500,
            in_person: 3000
          },
          bio: 'Experienced criminal lawyer with 12+ years of practice. Specialized in white-collar crimes and family disputes.',
          isVerified: true,
          verificationStatus: 'approved',
          profile_pic: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?w=300&h=300&fit=crop&crop=face',
          education: [
            {
              qualification: 'LL.B. (Bachelor of Laws)',
              degreeCollege: 'Government Law College, Mumbai'
            }
          ],
          bar_council_number: 'MAH/1234/2011',
          chamber_address: 'Office No. 45, 3rd Floor, Legal Chambers, Fort, Mumbai - 400001',
          available_days: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
          time_slots: {
            'Monday': ['09:00 AM', '10:00 AM', '11:00 AM', '02:00 PM', '03:00 PM'],
            'Tuesday': ['09:00 AM', '10:00 AM', '11:00 AM', '02:00 PM', '03:00 PM'],
            'Wednesday': ['09:00 AM', '10:00 AM', '11:00 AM', '02:00 PM', '03:00 PM'],
            'Thursday': ['09:00 AM', '10:00 AM', '11:00 AM', '02:00 PM', '03:00 PM'],
            'Friday': ['09:00 AM', '10:00 AM', '11:00 AM', '02:00 PM', '03:00 PM']
          },
          isOnline: true,
          availability: 'online',
          availability_status: 'online',
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        },
        {
          full_name: 'Adv. Rajesh Kumar',
          fullName: 'Adv. Rajesh Kumar',
          email: 'rajesh.kumar@example.com',
          phone: '+91 98765 43211',
          specialization: ['Corporate Law', 'Business Law'],
          bar_council_state: 'Delhi, NCR',
          location: 'Delhi, NCR',
          experience_years: 8,
          average_rating: 4.5,
          consultation_fee: 2000,
          fees: {
            text: 1200,
            call: 1800,
            video: 2200,
            in_person: 2800
          },
          bio: 'Corporate lawyer specializing in business contracts, mergers, and acquisitions with 8+ years of experience.',
          isVerified: true,
          verificationStatus: 'approved',
          profile_pic: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=300&h=300&fit=crop&crop=face',
          education: [
            {
              qualification: 'LL.B. (Bachelor of Laws)',
              degreeCollege: 'Delhi University'
            }
          ],
          bar_council_number: 'DEL/5678/2015',
          chamber_address: 'Chamber No. 12, Supreme Court Bar Association, New Delhi - 110001',
          available_days: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
          time_slots: {
            'Monday': ['10:00 AM', '11:00 AM', '12:00 PM', '03:00 PM', '04:00 PM'],
            'Tuesday': ['10:00 AM', '11:00 AM', '12:00 PM', '03:00 PM', '04:00 PM'],
            'Wednesday': ['10:00 AM', '11:00 AM', '12:00 PM', '03:00 PM', '04:00 PM'],
            'Thursday': ['10:00 AM', '11:00 AM', '12:00 PM', '03:00 PM', '04:00 PM'],
            'Friday': ['10:00 AM', '11:00 AM', '12:00 PM', '03:00 PM', '04:00 PM'],
            'Saturday': ['10:00 AM', '11:00 AM', '12:00 PM']
          },
          isOnline: true,
          availability: 'online',
          availability_status: 'online',
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        },
        {
          full_name: 'Adv. Meera Patel',
          fullName: 'Adv. Meera Patel',
          email: 'meera.patel@example.com',
          phone: '+91 98765 43212',
          specialization: ['Property Law', 'Real Estate Law'],
          bar_council_state: 'Bangalore, Karnataka',
          location: 'Bangalore, Karnataka',
          experience_years: 15,
          average_rating: 4.9,
          consultation_fee: 3000,
          fees: {
            text: 1800,
            call: 2500,
            video: 3000,
            in_person: 3500
          },
          bio: 'Senior property lawyer with 15+ years of experience in real estate transactions, property disputes, and land acquisition.',
          isVerified: true,
          verificationStatus: 'approved',
          profile_pic: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=300&h=300&fit=crop&crop=face',
          education: [
            {
              qualification: 'LL.B. (Bachelor of Laws)',
              degreeCollege: 'National Law School of India University, Bangalore'
            }
          ],
          bar_council_number: 'KAR/9012/2008',
          chamber_address: 'Suite 25, High Court Complex, Bangalore - 560001',
          available_days: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
          time_slots: {
            'Monday': ['09:00 AM', '10:00 AM', '11:00 AM', '02:00 PM', '03:00 PM', '04:00 PM'],
            'Tuesday': ['09:00 AM', '10:00 AM', '11:00 AM', '02:00 PM', '03:00 PM', '04:00 PM'],
            'Wednesday': ['09:00 AM', '10:00 AM', '11:00 AM', '02:00 PM', '03:00 PM', '04:00 PM'],
            'Thursday': ['09:00 AM', '10:00 AM', '11:00 AM', '02:00 PM', '03:00 PM', '04:00 PM'],
            'Friday': ['09:00 AM', '10:00 AM', '11:00 AM', '02:00 PM', '03:00 PM', '04:00 PM']
          },
          isOnline: true,
          availability: 'online',
          availability_status: 'online',
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        }
      ];

      // Create each sample lawyer
      for (const lawyerData of sampleLawyers) {
        const lawyerRef = await addDoc(collection(db, 'lawyers'), lawyerData);
        console.log(`✅ Created sample lawyer: ${lawyerRef.id} - ${lawyerData.full_name}`);
      }

      console.log('🎉 Sample lawyers created successfully!');
      return true;
    } catch (error) {
      console.error('Error creating sample lawyers:', error);
      return false;
    }
  }
};

// Subscription Service
export const subscriptionService = {
  // Create subscription
  async createSubscription(subscriptionData) {
    try {
      const subscriptionRef = await addDoc(collection(db, 'subscriptions'), {
        ...subscriptionData,
        status: 'active',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      return { success: true, data: { id: subscriptionRef.id, ...subscriptionData } };
    } catch (error) {
      console.error('Create subscription error:', error);
      return { success: false, error: error.message };
    }
  },

  // Create free trial subscription
  async createFreeTrial() {
    try {
      // Get current user from Firebase Auth
      const { getAuth } = await import('firebase/auth');
      const auth = getAuth();
      const currentUser = auth.currentUser;
      
      if (!currentUser) {
        throw new Error('No authenticated user found');
      }

      // Check if user already has an active subscription
      const existingSubscription = await this.getCurrentUserSubscription();
      if (existingSubscription.success && existingSubscription.data) {
        return { success: false, error: 'User already has an active subscription' };
      }

      // Create free trial subscription
      const freeTrialData = {
        userId: currentUser.uid,
        planId: 'free',
        planName: 'Free Plan',
        planType: 'free',
        price: 0,
        currency: 'INR',
        status: 'active',
        startDate: new Date().toISOString(),
        endDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(), // 30 days from now
        features: [
          '10 consultation per month (chat only)',
          'Basic legal document templates',
          'Access to legal FAQ section',
          'Community forum access',
          'Email support (48-hour response)'
        ],
        consultationsRemaining: 10,
        consultationsUsed: 0
      };

      const subscriptionRef = await addDoc(collection(db, 'subscriptions'), {
        ...freeTrialData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });

      return { 
        success: true, 
        data: { id: subscriptionRef.id, ...freeTrialData } 
      };
    } catch (error) {
      console.error('Create free trial error:', error);
      return { success: false, error: error.message };
    }
  },

  // Get current user subscription
  async getCurrentUserSubscription() {
    try {
      // Get current user from Firebase Auth
      const { getAuth } = await import('firebase/auth');
      const auth = getAuth();
      const currentUser = auth.currentUser;
      
      if (!currentUser) {
        return { success: false, error: 'No authenticated user found' };
      }

      // Simplified query to avoid index requirements
      const subscriptionQuery = query(
        collection(db, 'subscriptions'),
        where('userId', '==', currentUser.uid),
        where('status', '==', 'active')
      );
      const querySnapshot = await getDocs(subscriptionQuery);
      
      if (!querySnapshot.empty) {
        // Sort in memory to get the most recent
        const subscriptions = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        const sortedSubscriptions = subscriptions.sort((a, b) => {
          const aTime = a.createdAt?.toDate?.() || new Date(a.createdAt) || new Date(0);
          const bTime = b.createdAt?.toDate?.() || new Date(b.createdAt) || new Date(0);
          return bTime - aTime;
        });
        
        return { success: true, data: sortedSubscriptions[0] };
      }
      return { success: false, error: 'No active subscription found' };
    } catch (error) {
      console.error('Get current user subscription error:', error);
      return { success: false, error: error.message };
    }
  },

  // Get user subscription (alias for backward compatibility)
  async getUserSubscription(userId) {
    try {
      const subscriptionQuery = query(
        collection(db, 'subscriptions'),
        where('userId', '==', userId),
        where('status', '==', 'active')
      );
      const querySnapshot = await getDocs(subscriptionQuery);
      
      if (!querySnapshot.empty) {
        // Sort in memory to get the most recent
        const subscriptions = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        const sortedSubscriptions = subscriptions.sort((a, b) => {
          const aTime = a.createdAt?.toDate?.() || new Date(a.createdAt) || new Date(0);
          const bTime = b.createdAt?.toDate?.() || new Date(b.createdAt) || new Date(0);
          return bTime - aTime;
        });
        
        return { success: true, data: sortedSubscriptions[0] };
      }
      return { success: false, error: 'No active subscription found' };
    } catch (error) {
      console.error('Get user subscription error:', error);
      return { success: false, error: error.message };
    }
  },

  // Update subscription
  async updateSubscription(subscriptionId, updates) {
    try {
      const subscriptionRef = doc(db, 'subscriptions', subscriptionId);
      await updateDoc(subscriptionRef, {
        ...updates,
        updatedAt: serverTimestamp()
      });
      return { success: true };
    } catch (error) {
      console.error('Update subscription error:', error);
      return { success: false, error: error.message };
    }
  },

  // Cancel subscription
  async cancelSubscription(subscriptionId) {
    try {
      const subscriptionRef = doc(db, 'subscriptions', subscriptionId);
      await updateDoc(subscriptionRef, {
        status: 'cancelled',
        cancelledAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      return { success: true };
    } catch (error) {
      console.error('Cancel subscription error:', error);
      return { success: false, error: error.message };
    }
  },

  // Check if user can use a feature
  async canUseFeature(featureType) {
    try {
      const currentSubscription = await this.getCurrentUserSubscription();
      if (!currentSubscription.success || !currentSubscription.data) {
        return { success: false, data: false, error: 'No active subscription' };
      }

      const subscription = currentSubscription.data;
      
      switch (featureType) {
        case 'consultation':
          return { 
            success: true, 
            data: subscription.consultationsRemaining > 0 
          };
        case 'video_call':
          return { 
            success: true, 
            data: subscription.planType !== 'free' && subscription.consultationsRemaining > 0 
          };
        case 'document_review':
          return { 
            success: true, 
            data: subscription.planType !== 'free' 
          };
        case 'ai_assistant':
          return { 
            success: true, 
            data: true // All plans can use AI assistant
          };
        default:
          return { success: false, data: false, error: 'Unknown feature type' };
      }
    } catch (error) {
      console.error('Can use feature error:', error);
      return { success: false, data: false, error: error.message };
    }
  },

  // Record feature usage
  async recordFeatureUsage(featureType) {
    try {
      const currentSubscription = await this.getCurrentUserSubscription();
      if (!currentSubscription.success || !currentSubscription.data) {
        return { success: false, error: 'No active subscription' };
      }

      const subscription = currentSubscription.data;
      
      switch (featureType) {
        case 'consultation':
          if (subscription.consultationsRemaining <= 0) {
            return { success: false, error: 'No consultations remaining' };
          }
          await this.updateSubscription(subscription.id, {
            consultationsRemaining: subscription.consultationsRemaining - 1,
            consultationsUsed: subscription.consultationsUsed + 1
          });
          break;
        case 'video_call':
          if (subscription.planType === 'free') {
            return { success: false, error: 'Video calls not available in free plan' };
          }
          if (subscription.consultationsRemaining <= 0) {
            return { success: false, error: 'No consultations remaining' };
          }
          await this.updateSubscription(subscription.id, {
            consultationsRemaining: subscription.consultationsRemaining - 1,
            consultationsUsed: subscription.consultationsUsed + 1
          });
          break;
        case 'document_review':
          if (subscription.planType === 'free') {
            return { success: false, error: 'Document reviews not available in free plan' };
          }
          break;
        default:
          return { success: false, error: 'Unknown feature type' };
      }
      
      return { success: true };
    } catch (error) {
      console.error('Record feature usage error:', error);
      return { success: false, error: error.message };
    }
  },

  // Upgrade subscription
  async upgradeSubscription(planId, paymentMethod, amount) {
    try {
      const currentSubscription = await this.getCurrentUserSubscription();
      if (!currentSubscription.success || !currentSubscription.data) {
        return { success: false, error: 'No active subscription to upgrade' };
      }

      const subscription = currentSubscription.data;
      
      // Get current user for payment tracking
      const { getAuth } = await import('firebase/auth');
      const auth = getAuth();
      const currentUser = auth.currentUser;
      
      if (!currentUser) {
        return { success: false, error: 'No authenticated user found' };
      }
      
      // Update subscription with new plan details
      const planDetails = {
        'pro': {
          planName: 'Pro Plan',
          planType: 'pro',
          price: 999,
          consultationsRemaining: 999, // Unlimited
          features: [
            'Unlimited chat consultations',
            '3 video/call consultations per month',
            'Priority lawyer matching',
            'Advanced legal document templates',
            'Case tracking dashboard',
            'Legal document review (2 docs/month)',
            'Priority email support (24-hour response)',
            'Phone support'
          ]
        },
        'pro-plus': {
          planName: 'Pro+ Plan',
          planType: 'pro-plus',
          price: 1999,
          consultationsRemaining: 999, // Unlimited
          features: [
            'Everything in Pro',
            'Unlimited video/call consultations',
            'Dedicated legal advisor',
            'Unlimited document reviews',
            'Court filing assistance',
            'Legal research reports',
            'Emergency legal support (24/7)',
            'Contract drafting service',
            'Priority booking (same-day slots)'
          ]
        },
        'exclusive': {
          planName: 'Exclusive Plan',
          planType: 'exclusive',
          price: 4999,
          consultationsRemaining: 999, // Unlimited
          features: [
            'Everything in Pro+',
            'Access to High Court lawyers',
            'Access to Supreme Court lawyers',
            'Senior advocate consultations',
            'Constitutional law experts',
            'PIL (Public Interest Litigation) guidance',
            'Supreme Court case filing support',
            'Appellate court representation',
            'Legal opinion from senior counsel',
            'Direct access to law chambers',
            'Expedited case preparation'
          ]
        }
      };

      const newPlan = planDetails[planId];
      if (!newPlan) {
        return { success: false, error: 'Invalid plan ID' };
      }

      // Create payment record for admin tracking
      const paymentData = {
        userId: currentUser.uid,
        userEmail: currentUser.email,
        subscriptionId: subscription.id,
        planId: planId,
        planName: newPlan.planName,
        amount: amount || newPlan.price,
        currency: 'INR',
        paymentMethod: paymentMethod || 'online',
        status: 'completed',
        type: 'subscription_upgrade',
        description: `Upgraded to ${newPlan.planName}`,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      const paymentRef = await addDoc(collection(db, 'payments'), paymentData);

      // Update subscription
      await this.updateSubscription(subscription.id, {
        planId: planId,
        planName: newPlan.planName,
        planType: newPlan.planType,
        price: newPlan.price,
        features: newPlan.features,
        consultationsRemaining: newPlan.consultationsRemaining,
        upgradedAt: serverTimestamp(),
        lastPaymentId: paymentRef.id
      });

      return { success: true, paymentId: paymentRef.id };
    } catch (error) {
      console.error('Upgrade subscription error:', error);
      return { success: false, error: error.message };
    }
  }
};

// Payment Service
export const paymentService = {
  // Create payment
  async createPayment(paymentData) {
    try {
      const paymentRef = await addDoc(collection(db, 'payments'), {
        ...paymentData,
        status: 'pending',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      return { success: true, data: { id: paymentRef.id, ...paymentData } };
    } catch (error) {
      console.error('Create payment error:', error);
      return { success: false, error: error.message };
    }
  },

  // Process payment and confirm booking
  async processPaymentAndConfirmBooking(paymentData, appointmentData) {
    try {
      // Create payment record
      const paymentRef = await addDoc(collection(db, 'payments'), {
        ...paymentData,
        status: 'completed',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });

      // Create appointment with confirmed status
      const appointmentRef = await addDoc(collection(db, 'appointments'), {
        ...appointmentData,
        status: 'confirmed',
        paymentId: paymentRef.id,
        paymentStatus: 'completed',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });

      return { 
        success: true, 
        data: { 
          appointmentId: appointmentRef.id, 
          paymentId: paymentRef.id,
          appointment: { id: appointmentRef.id, ...appointmentData },
          payment: { id: paymentRef.id, ...paymentData }
        } 
      };
    } catch (error) {
      console.error('Process payment error:', error);
      return { success: false, error: error.message };
    }
  },

  // Get payment by ID
  async getPaymentById(paymentId) {
    try {
      const paymentDoc = await getDoc(doc(db, 'payments', paymentId));
      if (paymentDoc.exists()) {
        return { success: true, data: { id: paymentDoc.id, ...paymentDoc.data() } };
      }
      return { success: false, error: 'Payment not found' };
    } catch (error) {
      console.error('Get payment error:', error);
      return { success: false, error: error.message };
    }
  },

  // Get user payments
  async getUserPayments(userId) {
    try {
      const paymentsQuery = query(
        collection(db, 'payments'),
        where('userId', '==', userId)
      );
      const querySnapshot = await getDocs(paymentsQuery);
      const payments = [];
      querySnapshot.forEach((doc) => {
        payments.push({ id: doc.id, ...doc.data() });
      });
      
      // Sort in memory to avoid index requirements
      payments.sort((a, b) => {
        const aTime = a.createdAt?.toDate?.() || new Date(a.createdAt) || new Date(0);
        const bTime = b.createdAt?.toDate?.() || new Date(b.createdAt) || new Date(0);
        return bTime - aTime;
      });
      
      return { success: true, data: payments };
    } catch (error) {
      console.error('Get user payments error:', error);
      return { success: false, error: error.message };
    }
  },

  // Get all payments for admin panel
  async getAllPayments() {
    try {
      const paymentsQuery = query(collection(db, 'payments'));
      const querySnapshot = await getDocs(paymentsQuery);
      const payments = [];
      querySnapshot.forEach((doc) => {
        payments.push({ id: doc.id, ...doc.data() });
      });
      
      // Sort in memory to avoid index requirements
      payments.sort((a, b) => {
        const aTime = a.createdAt?.toDate?.() || new Date(a.createdAt) || new Date(0);
        const bTime = b.createdAt?.toDate?.() || new Date(b.createdAt) || new Date(0);
        return bTime - aTime;
      });
      
      return { success: true, data: payments };
    } catch (error) {
      console.error('Get all payments error:', error);
      return { success: false, error: error.message };
    }
  },

  // Get payments by type for admin panel
  async getPaymentsByType(paymentType) {
    try {
      const paymentsQuery = query(
        collection(db, 'payments'),
        where('type', '==', paymentType)
      );
      const querySnapshot = await getDocs(paymentsQuery);
      const payments = [];
      querySnapshot.forEach((doc) => {
        payments.push({ id: doc.id, ...doc.data() });
      });
      
      // Sort in memory to avoid index requirements
      payments.sort((a, b) => {
        const aTime = a.createdAt?.toDate?.() || new Date(a.createdAt) || new Date(0);
        const bTime = b.createdAt?.toDate?.() || new Date(b.createdAt) || new Date(0);
        return bTime - aTime;
      });
      
      return { success: true, data: payments };
    } catch (error) {
      console.error('Get payments by type error:', error);
      return { success: false, error: error.message };
    }
  },

  // Update payment status
  async updatePaymentStatus(paymentId, status) {
    try {
      const paymentRef = doc(db, 'payments', paymentId);
      await updateDoc(paymentRef, {
        status: status,
        updatedAt: serverTimestamp()
      });
      return { success: true };
    } catch (error) {
      console.error('Update payment status error:', error);
      return { success: false, error: error.message };
    }
  }
};

// Storage Service
export const storageService = {
  // Upload file
  async uploadFile(file, path) {
    try {
      const storageRef = ref(storage, path);
      const snapshot = await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(snapshot.ref);
      return { success: true, data: downloadURL };
    } catch (error) {
      console.error('Upload file error:', error);
      return { success: false, error: error.message };
    }
  },

  // Delete file
  async deleteFile(path) {
    try {
      const storageRef = ref(storage, path);
      await deleteObject(storageRef);
      return { success: true };
    } catch (error) {
      console.error('Delete file error:', error);
      return { success: false, error: error.message };
    }
  }
}; 

// Rating and Review Service
export const ratingService = {
  // Submit a rating and review for a lawyer
  async submitRating(ratingData) {
    try {
      const { db } = await import('../config/firebase');
      const { collection, addDoc, updateDoc, doc, getDoc, query, where, getDocs, serverTimestamp } = await import('firebase/firestore');
      
      // Create the rating document
      const ratingRef = await addDoc(collection(db, 'ratings'), {
        lawyerId: ratingData.lawyerId,
        clientId: ratingData.clientId,
        bookingId: ratingData.bookingId,
        rating: ratingData.rating,
        feedback: ratingData.feedback,
        consultationType: ratingData.consultationType,
        clientName: ratingData.clientName,
        lawyerName: ratingData.lawyerName,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });

      // Update the booking to mark it as rated
      if (ratingData.bookingId) {
        try {
          // Try to update in bookings collection first
          const bookingRef = doc(db, 'bookings', ratingData.bookingId);
          await updateDoc(bookingRef, {
            hasRated: true,
            clientRating: ratingData.rating,
            ratingId: ratingRef.id,
            ratingDate: new Date().toISOString(),
            updatedAt: serverTimestamp()
          });
        } catch (bookingError) {
          console.warn('Could not update booking with rating:', bookingError);
          // Try appointments collection as fallback
          try {
            const appointmentRef = doc(db, 'appointments', ratingData.bookingId);
            await updateDoc(appointmentRef, {
              hasRated: true,
              rating: ratingData.rating,
              ratingId: ratingRef.id,
              ratingDate: new Date().toISOString(),
              updatedAt: serverTimestamp()
            });
          } catch (appointmentError) {
            console.warn('Could not update appointment with rating:', appointmentError);
          }
        }
      }

      // Update lawyer's average rating and total reviews
      await this.updateLawyerRatingStats(ratingData.lawyerId);

      return { 
        success: true, 
        data: { 
          id: ratingRef.id, 
          ...ratingData 
        } 
      };
    } catch (error) {
      console.error('Error submitting rating:', error);
      return { success: false, error: error.message };
    }
  },

  // Update lawyer's rating statistics
  async updateLawyerRatingStats(lawyerId) {
    try {
      const { db } = await import('../config/firebase');
      const { collection, query, where, getDocs, updateDoc, doc, serverTimestamp } = await import('firebase/firestore');
      
      // Get all ratings for this lawyer
      const ratingsQuery = query(
        collection(db, 'ratings'),
        where('lawyerId', '==', lawyerId)
      );
      
      const ratingsSnapshot = await getDocs(ratingsQuery);
      const ratings = ratingsSnapshot.docs.map(doc => doc.data());
      
      if (ratings.length === 0) return;

      // Calculate statistics
      const totalRating = ratings.reduce((sum, r) => sum + r.rating, 0);
      const averageRating = totalRating / ratings.length;
      
      const ratingDistribution = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };
      ratings.forEach(rating => {
        ratingDistribution[rating.rating]++;
      });

      // Update lawyer document
      const lawyerRef = doc(db, 'lawyers', lawyerId);
              await updateDoc(lawyerRef, {
          rating: parseFloat(averageRating.toFixed(1)),
          average_rating: parseFloat(averageRating.toFixed(1)), // Add this for compatibility
          totalReviews: ratings.length,
          ratingDistribution,
          lastRatingUpdate: serverTimestamp()
        });

      return { success: true };
    } catch (error) {
      console.error('Error updating lawyer rating stats:', error);
      return { success: false, error: error.message };
    }
  },

  // Get all ratings for a lawyer
  async getLawyerRatings(lawyerId) {
    try {
      const { db } = await import('../config/firebase');
      const { collection, query, where, orderBy, getDocs } = await import('firebase/firestore');
      
      const ratingsQuery = query(
        collection(db, 'ratings'),
        where('lawyerId', '==', lawyerId),
        orderBy('createdAt', 'desc')
      );
      
      const ratingsSnapshot = await getDocs(ratingsQuery);
      const ratings = ratingsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      return { success: true, data: ratings };
    } catch (error) {
      console.error('Error getting lawyer ratings:', error);
      return { success: false, error: error.message };
    }
  },

  // Get lawyer rating statistics
  async getLawyerRatingStats(lawyerId) {
    try {
      const { db } = await import('../config/firebase');
      const { doc, getDoc } = await import('firebase/firestore');
      
      const lawyerDoc = await getDoc(doc(db, 'lawyers', lawyerId));
      if (lawyerDoc.exists()) {
        const lawyerData = lawyerDoc.data();
        return {
          success: true,
                      data: {
              rating: lawyerData.rating || lawyerData.average_rating || 0,
              totalReviews: lawyerData.totalReviews || 0,
              ratingDistribution: lawyerData.ratingDistribution || { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 }
            }
        };
      }
      
      return { success: false, error: 'Lawyer not found' };
    } catch (error) {
      console.error('Error getting lawyer rating stats:', error);
      return { success: false, error: error.message };
    }
  },

  // Add lawyer reply to a rating
  async addLawyerReply(ratingId, replyText) {
    try {
      const { db } = await import('../config/firebase');
      const { doc, updateDoc, serverTimestamp } = await import('firebase/firestore');
      
      const ratingRef = doc(db, 'ratings', ratingId);
      await updateDoc(ratingRef, {
        lawyerReply: replyText,
        lawyerReplyDate: serverTimestamp(),
        updatedAt: serverTimestamp()
      });

      return { success: true };
    } catch (error) {
      console.error('Error adding lawyer reply:', error);
      return { success: false, error: error.message };
    }
  },

  // Delete lawyer reply
  async deleteLawyerReply(ratingId) {
    try {
      const { db } = await import('../config/firebase');
      const { doc, updateDoc, serverTimestamp } = await import('firebase/firestore');
      
      const ratingRef = doc(db, 'ratings', ratingId);
      await updateDoc(ratingRef, {
        lawyerReply: null,
        lawyerReplyDate: null,
        updatedAt: serverTimestamp()
      });

      return { success: true };
    } catch (error) {
      console.error('Error deleting lawyer reply:', error);
      return { success: false, error: error.message };
    }
  },

  // Report a rating
  async reportRating(ratingId, reason) {
    try {
      const { db } = await import('../config/firebase');
      const { doc, updateDoc, serverTimestamp } = await import('firebase/firestore');
      
      const ratingRef = doc(db, 'ratings', ratingId);
      await updateDoc(ratingRef, {
        reported: true,
        reportReason: reason,
        reportDate: serverTimestamp(),
        updatedAt: serverTimestamp()
      });

      return { success: true };
    } catch (error) {
      console.error('Error reporting rating:', error);
      return { success: false, error: error.message };
    }
  },

  // Get all ratings for a client
  async getClientRatings(clientId) {
    try {
      const { db } = await import('../config/firebase');
      const { collection, query, where, orderBy, getDocs } = await import('firebase/firestore');
      
      const ratingsQuery = query(
        collection(db, 'ratings'),
        where('clientId', '==', clientId),
        orderBy('createdAt', 'desc')
      );
      
      const ratingsSnapshot = await getDocs(ratingsQuery);
      const ratings = ratingsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      return { success: true, data: ratings };
    } catch (error) {
      console.error('Error getting client ratings:', error);
      return { success: false, error: error.message };
    }
  }
}; 